// Getting Started checklist tokens. Step keys mirror the backend
// (CleaningValidation.Domain.Constants.OnboardingSteps); labels, routes and prerequisites live here.
import { PERMISSIONS } from './permissions';

export const ONBOARDING_STEPS = {
  REVIEW_SETTINGS: 'reviewSettings',
  INVITE_TEAM: 'inviteTeam',
  ADD_EQUIPMENT: 'addEquipment',
  ADD_CLEANING_AGENTS: 'addCleaningAgents',
  ADD_HBELS: 'addHbels',
  ADD_PRODUCT: 'addProduct',
  ADD_ANALYTICAL_METHODS: 'addAnalyticalMethods',
  CREATE_FMECA: 'createFmeca',
  SUBMIT_FMECA: 'submitFmeca',
};

// Mirrors CleaningValidation.Domain.Enums.OnboardingState (sent/received as names).
export const ONBOARDING_STATES = {
  EXPANDED: 'Expanded',
  MINIMISED: 'Minimised',
  DISMISSED: 'Dismissed',
};

export const ONBOARDING_PHASES = {
  1: 'Set up your workspace',
  2: 'Build your registers',
  3: 'Run your first assessment',
};

// Query flag a registry list reads to open its bulk-import dialog on arrival.
export const BULK_IMPORT_PARAM = 'import';

// Per-step copy and navigation. `route` opens the work, `importRoute` (registries with AI bulk
// import) opens the list with the import dialog, `needs` lists prerequisite steps, `noun` is how
// the step reads inside a "Needs …" hint.
export const ONBOARDING_ITEMS = {
  [ONBOARDING_STEPS.REVIEW_SETTINGS]: {
    label: 'Review organisation settings',
    description: 'Check units, lookup values and which review gates your assessments go through.',
    actionLabel: 'Open settings',
    route: '/org-settings',
    doneMeta: () => 'Reviewed',
    todoMeta: 'Units, lookups, review workflow',
  },
  [ONBOARDING_STEPS.INVITE_TEAM]: {
    label: 'Invite your team and assign reviewers',
    description: 'Invite colleagues and give QA approvers the Reviewer role.',
    actionLabel: 'Invite team',
    route: '/team',
    doneMeta: (n) => `${n} member${n === 1 ? '' : 's'} invited`,
    todoMeta: 'Optional',
  },
  [ONBOARDING_STEPS.ADD_EQUIPMENT]: {
    label: 'Add equipment',
    description: 'Register the equipment you clean, with its product contact surface area.',
    actionLabel: 'Add equipment',
    route: '/equipment/new',
    importRoute: `/equipment?${BULK_IMPORT_PARAM}=1`,
    noun: 'equipment',
  },
  [ONBOARDING_STEPS.ADD_CLEANING_AGENTS]: {
    label: 'Add cleaning agents',
    description: 'Add the detergents and disinfectants used in your cleaning procedures.',
    actionLabel: 'Add cleaning agent',
    route: '/cleaning-agents/new',
    importRoute: `/cleaning-agents?${BULK_IMPORT_PARAM}=1`,
    noun: 'a cleaning agent',
  },
  [ONBOARDING_STEPS.ADD_HBELS]: {
    label: 'Add HBELs',
    description: 'Record the health-based exposure limits for the actives you manufacture.',
    actionLabel: 'Add HBEL',
    route: '/hbels/new',
    noun: 'HBELs',
  },
  [ONBOARDING_STEPS.ADD_PRODUCT]: {
    label: 'Add a product with its equipment train',
    description: 'Link the product to its HBELs and the equipment it runs on. Assessments are built from this.',
    actionLabel: 'Add product',
    route: '/products/new',
    importRoute: `/products?${BULK_IMPORT_PARAM}=1`,
    needs: [ONBOARDING_STEPS.ADD_EQUIPMENT, ONBOARDING_STEPS.ADD_HBELS],
    noun: 'a product',
  },
  [ONBOARDING_STEPS.ADD_ANALYTICAL_METHODS]: {
    label: 'Add analytical methods',
    description: 'Add the methods used to detect residues, with their LOQ, LOD and recovery.',
    actionLabel: 'Add method',
    route: '/analytical-methods/new',
    importRoute: `/analytical-methods?${BULK_IMPORT_PARAM}=1`,
  },
  [ONBOARDING_STEPS.CREATE_FMECA]: {
    label: 'Create your first FMECA',
    description: 'Assess a product and cleaning agent pair step by step across its equipment train.',
    actionLabel: 'Create FMECA',
    route: '/fmeca-assessment/new',
    needs: [ONBOARDING_STEPS.ADD_PRODUCT, ONBOARDING_STEPS.ADD_CLEANING_AGENTS],
    noun: 'an FMECA',
    doneMeta: (n) => `${n} created`,
  },
  [ONBOARDING_STEPS.SUBMIT_FMECA]: {
    label: 'Submit it for review',
    description: 'Send the FMECA to its reviewers. Once approved, qualifying risks reach the Risk Register.',
    actionLabel: 'Open FMECA assessments',
    route: '/fmeca-assessment',
    needs: [ONBOARDING_STEPS.CREATE_FMECA],
    doneMeta: () => 'Submitted',
    todoMeta: 'Risks reach the Risk Register',
  },
};

// Any of these lets a member act on at least one checklist item (mirrors the backend filter).
export const ONBOARDING_EDIT_PERMISSIONS = [
  PERMISSIONS.SETTINGS_EDIT,
  PERMISSIONS.TEAM_EDIT,
  PERMISSIONS.EQUIPMENT_EDIT,
  PERMISSIONS.CLEANING_AGENTS_EDIT,
  PERMISSIONS.HBELS_EDIT,
  PERMISSIONS.PRODUCTS_EDIT,
  PERMISSIONS.ANALYTICAL_METHODS_EDIT,
  PERMISSIONS.FMECA_EDIT,
];

// Mirrors CleaningValidation.Domain.Enums.SiteType (sent as names).
export const SITE_TYPES = [
  { value: 'OralSolidDose', label: 'Oral solid dose' },
  { value: 'ActivePharmaceuticalIngredient', label: 'Active pharmaceutical ingredient (API)' },
  { value: 'SterileParenteral', label: 'Sterile / parenteral' },
  { value: 'Biologics', label: 'Biologics' },
  { value: 'ContractManufacturer', label: 'Contract manufacturer (CDMO)' },
  { value: 'Other', label: 'Other' },
];
